import {
  Badge,
  Button,
  Dropdown,
  Menu,
  Table,
  TableColumnsType,
  TableProps,
  Tooltip,
  message,
} from 'antd';
import { What, chunksRender, getChunks, styles } from 'views/Marketing/components/PolicyTable';
import {
  isDayRepeatTermValidity,
  isLongTermValidity,
  isRangeTermValidity,
  isWeekRepeatTermValidity,
  isYearRepeatTermValidity,
  useTermOfValidityRender,
} from './PolicyForm/share';
import { DownOutlined } from '@ant-design/icons';
import StatusSwitch from 'components/StatusSwitch';
import { regionTimeZoneMap } from 'services/config/region';
import toLowerCase from 'utils/toLowerCase';
import dayjs, { UnitTypeLongPlural } from 'lib/dayjs';
import { deletePolicy, updatePolicy, updatePolicyPriority } from './service';
import { WeekValuable } from 'components/MultiTimeSelect/WeeKGroup';
import { PolicyName } from 'views/Aide/Scenario/NewPolicy/useGetPolicyName';

type RT = NightWatch.Policy;

export interface PolicyTableProps extends TableProps<RT> {
  editable?: boolean;
  platform?: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  onEdit: (record: RT) => void;
  onUpdated: () => Promise<any>;
}

function PolicyTable(props: PolicyTableProps) {
  const { editable = true, platform, region, dataSource, onEdit, onUpdated, ...otherProps } = props;

  const { t, termOfValidityRender } = useTermOfValidityRender();

  const { [toLowerCase(region)]: timeZone } = regionTimeZoneMap;

  const columns: TableColumnsType<RT> = [
    {
      key: 'priority',
      dataIndex: 'priority',
      width: 120,
      title: t('aide.执行顺序'),
      sorter: true,
    },
    {
      key: 'name',
      dataIndex: 'name',
      width: 150,
      title: t('marketing.策略名称'),
      render: (name: string) => <PolicyName name={name} />,
    },
    {
      key: 'replyContent',
      dataIndex: 'replyContent',
      width: 300,
      title: t('reviewSetting.发送内容'),
      render: ([replyContent = '']: RT['replyContent']) =>
        chunksRender(getChunks({ content: replyContent, translatedContent: '' }), t),
    },
    {
      key: 'termOfValidity',
      dataIndex: 'termOfValidity',
      width: 250,
      title: t('marketing.有效期'),
      render: termOfValidityRender,
    },
    {
      key: 'status',
      width: 120,
      title: (
        <div>
          {t('aide.策略状态')}
          <Tooltip
            overlayClassName={styles.tooltip}
            title={
              <div>
                {`${t('marketing.生效中')}: ${t('aide.策略开关为开，且当前时间在策略有效期内')}`}
                <br />
                {`${t('marketing.未开始')}: ${t(
                  'aide.策略开关为开，且当前时间在策略生效开始时间之前',
                )}`}
                <br />
                {`${t('marketing.已过期')}: ${t(
                  'aide.策略开关为开，且当前时间在策略生效结束时间之后',
                )}`}
                <br />
                {`${t('marketing.未启用')}: ${t('aide.策略开关为关')}`}
              </div>
            }
          >
            <What />
          </Tooltip>
        </div>
      ),
      render: (_, { enable, termOfValidity }) => {
        if (!enable) {
          return <Badge status="warning" text={t('marketing.未启用')} />;
        }

        const status = termOfValidityStatus(termOfValidity, timeZone);

        if (!status) {
          return '-';
        }

        return {
          waiting: <Badge color="geekblue" text={t('marketing.未开始')} />,
          expired: <Badge status="default" text={t('marketing.已过期')} />,
          valid: <Badge status="success" text={t('marketing.生效中')} />,
        }[status];
      },
    },
    {
      key: 'enable',
      dataIndex: 'enable',
      width: 120,
      title: t('aide.策略开关'),
      render: (enable: RT['enable'], record) => {
        return (
          <StatusSwitch
            disabled={!editable}
            checked={enable}
            onChange={(enable) => {
              return updatePolicy({
                ...record,
                enable,
              })
                .then(() => onUpdated())
                .then(() => {
                  message.success(t('operation.saveSucceed'));
                })
                .catch(() => {
                  message.error(t('marketingLabel.保存失败，请稍后再试'));
                });
            }}
            checkedChildren={t('aide.开')}
            unCheckedChildren={t('aide.关')}
          />
        );
      },
    },
    {
      key: 'operation',
      title: t('操作'),
      width: 200,
      fixed: 'right',
      render: (_, record: RT) => {
        const { priority, fallbackPolicyId } = record;

        const isFirst = priority === 1;
        const isLast = priority === (dataSource || []).length;

        return (
          <div>
            <Button
              disabled={!editable}
              type="link"
              onClick={() => onEdit(record)}
              style={{ paddingLeft: 0 }}
            >
              {t('marketing.编辑')}
            </Button>
            <Dropdown
              disabled={!editable}
              overlay={
                <Menu
                  onClick={({ key }) => {
                    if (key === 'delete') {
                      deletePolicy(fallbackPolicyId)
                        .then(() => onUpdated())
                        .then(() => {
                          message.success(t('phrase.删除成功'));
                        })
                        .catch(() => {
                          message.error(t('phrase.删除失败，请稍后再试'));
                        });

                      return;
                    }

                    updatePolicyPriority({
                      fallbackPolicyId,
                      priorityActionType: key as any,
                    })
                      .then(() => onUpdated())
                      .then(() => {
                        message.success(t('operation.saveSucceed'));
                      })
                      .catch(() => {
                        message.error(t('operation.saveFailed'));
                      });
                  }}
                >
                  {!isFirst && (
                    <>
                      <Menu.Item key="TOP">{t('marketing.置顶')}</Menu.Item>
                      <Menu.Item key="UP">{t('marketing.上移')}</Menu.Item>
                    </>
                  )}
                  {!isLast && (
                    <>
                      <Menu.Item key="DOWN">{t('marketing.下移')}</Menu.Item>
                      <Menu.Item key="BOTTOM">{t('marketing.置底')}</Menu.Item>
                    </>
                  )}
                  <Menu.Item key="delete">{t('marketing.删除')}</Menu.Item>
                </Menu>
              }
            >
              <Button type="link">
                {t('marketing.更多')}
                <DownOutlined />
              </Button>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <Table
      rowKey="fallbackPolicyId"
      scroll={{ x: '100%' }}
      columns={columns}
      dataSource={dataSource}
      pagination={false}
      {...otherProps}
    />
  );
}

function termOfValidityStatusWithoutTry(
  termOfValidity: NightWatch.TermOfValidity,
  timeZone: string,
) {
  const valid = 'valid' as const;

  if (isLongTermValidity(termOfValidity)) {
    return valid;
  }

  const now = dayjs();

  const isBefore = (start: string, unit: UnitTypeLongPlural) =>
    now.isBefore(dayjs.tz(start, timeZone).startOf(unit));

  const isAfter = (end: string, unit: UnitTypeLongPlural) =>
    now.isAfter(dayjs.tz(end, timeZone).endOf(unit));

  const waiting = 'waiting' as const;
  const expired = 'expired' as const;

  if (isRangeTermValidity(termOfValidity)) {
    const {
      termRange: {
        dtRange: { startAt, endAt },
      },
    } = termOfValidity;

    const getDate = (time: string) => `${time}:00`;

    if (isBefore(getDate(startAt), 'minutes')) {
      return waiting;
    }

    if (isAfter(getDate(endAt), 'minutes')) {
      return expired;
    }

    return valid;
  }

  if (isYearRepeatTermValidity(termOfValidity)) {
    const {
      termRange: { yearRepeat },
    } = termOfValidity;

    const year = now.format('YYYY');

    const getDate = (time: string) => `${year}-${time}:00`;

    if (
      yearRepeat.some(
        ({ startAt, endAt }) =>
          isAfter(getDate(startAt), 'minutes') && (!endAt || isBefore(getDate(endAt), 'minutes')),
      )
    ) {
      return valid;
    }

    return waiting;
  }

  const {
    termRange: { dtRange },
  } = termOfValidity;

  if (dtRange && dtRange.startAt) {
    const { startAt, endAt } = dtRange;

    const getDate = (time: string) => `${time} 00:00:00`;

    if (isBefore(getDate(startAt), 'days')) {
      return waiting;
    }

    if (endAt && isAfter(getDate(endAt), 'days')) {
      return expired;
    }
  }

  const day = now.tz(timeZone).format('YYYY-MM-DD');

  const getDate = (time: string) => `${day} ${time}:00`;

  if (isDayRepeatTermValidity(termOfValidity)) {
    const {
      termRange: { dayRepeat },
    } = termOfValidity;

    if (
      dayRepeat.every(
        ({ startAt, endAt }) =>
          isBefore(getDate(startAt), 'minutes') || isAfter(getDate(endAt), 'minutes'),
      )
    ) {
      return waiting;
    }

    return valid;
  }

  if (isWeekRepeatTermValidity(termOfValidity)) {
    const {
      termRange: { weekRepeat },
    } = termOfValidity;

    if (
      weekRepeat.every(({ dayOfWeeks, timeRanges }) => {
        if (dayOfWeeks.every((week) => dayjs().tz(timeZone).get('day') !== WeekValuable[week])) {
          return true;
        }

        return timeRanges.every(
          ({ startAt, endAt }) =>
            isBefore(getDate(startAt), 'minutes') || isAfter(getDate(endAt), 'minutes'),
        );
      })
    ) {
      return waiting;
    }

    return valid;
  }

  return false;
}
function termOfValidityStatus(termOfValidity: NightWatch.TermOfValidity, timeZone: string) {
  try {
    return termOfValidityStatusWithoutTry(termOfValidity, timeZone);
  } catch (err) {
    console.log(err);

    return false;
  }
}

export default PolicyTable;
